import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { CheckIcon, TextSearch, XIcon, Calendar as CalendarIcon } from "lucide-react";
import { type HighlightArea } from "@react-pdf-viewer/highlight";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";

interface VerifyRowProps {
  caseItem: {
    id: string;
    date: string;
    party: string;
    summary: Array<{
      text: string;
      cites: Array<{
        text: string;
        page: number;
        highlightText: string;
        highlightAreas: HighlightArea[];
      }>;
    }>;
    verificationScore: number;
    futureDate: boolean;
    missingDay: boolean;
  };
  handleDateChange: (date: Date | null, caseId: string) => void;
  handlePartyChange: (event: React.ChangeEvent<HTMLInputElement>, caseId: string) => void;
  handleHighlight: (highlightAreas: HighlightArea[], page: number) => void;
}

export const VerifyRow: React.FC<VerifyRowProps> = ({
  caseItem,
  handleDateChange,
  handlePartyChange,
  handleHighlight,
}) => {
  const [date, setDate] = useState<Date | null>(caseItem.date ? parseISO(caseItem.date) : null);
  const [party, setParty] = useState<string>(caseItem.party);

  const onDateChange = (newDate: Date | null) => {
    setDate(newDate);
    handleDateChange(newDate, caseItem.id);
  };

  const onPartyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setParty(event.target.value);
    handlePartyChange(event, caseItem.id);
  };

  return (
    <tr key={caseItem.id}>
      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start text-left font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, "PPP, EEE") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={date ?? undefined}
              onSelect={newDate => onDateChange(newDate || null)}
              initialFocus
              month={date ?? undefined}
            />
          </PopoverContent>
        </Popover>
        <input
          type="text"
          value={party}
          onChange={onPartyChange}
          className="mt-4 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />
      </td>
      <td className="whitespace-normal break-words px-3 py-4 text-sm text-gray-500">
        {caseItem.summary.map((summaryItem, index) => (
          <div key={index}>
            {summaryItem.text}
            <div className="mt-2">
              {summaryItem.cites.map((cite, citeIndex) => (
                <div key={citeIndex} className="items-center space-x-2">
                  <div className="mb-2 block w-full">{cite.text}</div>
                  <button
                    onClick={() => handleHighlight(cite.highlightAreas, cite.page)}
                    className="inline-flex items-center gap-x-1.5 rounded-md border-2 border-[#eaebe6] bg-[#eaebe6] px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm hover:bg-indigo-700 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    <TextSearch className="flex" />
                    Pg.{cite.page}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </td>
      <td className="text-md whitespace-nowrap px-3 py-4 text-gray-500">
        {caseItem.verificationScore}%
        {caseItem.verificationScore <= 90 && (
          <div className="mt-2">
            <span className="flex-none rounded-full px-2 py-1 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-500">
              Low Verification Score
            </span>
          </div>
        )}
        {!caseItem.futureDate && (
          <div className="mt-2">
            <span className="mb-2 flex-none rounded-full px-2 py-1 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-500">
              Past date
            </span>
          </div>
        )}
        {caseItem.missingDay && (
          <div className="mt-2">
            <span className="flex-none rounded-full px-2 py-1 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-500">
              Missing day
            </span>
          </div>
        )}
      </td>
      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
        <div className="flex justify-end space-x-2">
          <button className="flex items-center rounded-md border-2 border-indigo-400 px-4 py-2 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-900">
            <CheckIcon className="mr-2 h-5 w-5" aria-hidden="true" />
            <span>Approve</span>
          </button>
          <button className="flex items-center text-red-600 hover:text-red-900">
            <XIcon className="mr-2 h-5 w-5" aria-hidden="true" />
            <span>Remove</span>
          </button>
        </div>
      </td>
    </tr>
  );
};
